(() => {
const DAY = 24 * 60 * 60 * 1000;

const moneyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 2
});

const toPence = (value) => Math.max(0, Math.round((Number(value) || 0) * 100));
const moneyFromPence = (pence) => moneyFormatter.format(Math.max(0, pence) / 100);
const money = (value) => moneyFromPence(toPence(value));
const offerNeedsValue = (offerType) => offerType === "percentage" || offerType === "fixed";

function discountValidationMessage(offerType, rawValue) {
  if (!offerNeedsValue(offerType)) return "";
  const value = Number(rawValue);
  if (!Number.isFinite(value) || value <= 0) {
    return offerType === "percentage"
      ? "Enter a percentage greater than 0 and no more than 100."
      : "Enter a cash discount greater than £0.";
  }
  if (offerType === "percentage" && value > 100) return "Enter a percentage no greater than 100.";
  return "";
}

const addFinding = (findings, severity, category, title, reason, action) => {
  findings.push({ severity, category, title, reason, action });
};

const severityRank = { red: 0, amber: 1, green: 2 };

function calculateBasketScenarios(input) {
  const validationMessage = discountValidationMessage(input.offerType, input.discountValue);
  if (validationMessage) throw new RangeError(validationMessage);
  const thresholdPence = toPence(input.minimumSpend);
  const fixedDiscountPence = toPence(input.discountValue);
  const percentageHundredths = Math.round((Number(input.discountValue) || 0) * 100);
  const baskets = thresholdPence > 0
    ? [Math.max(0, thresholdPence - 1), thresholdPence, thresholdPence + Math.max(1000, Math.round(thresholdPence * 0.25))]
    : [4000, 8000, 12000];

  return baskets.map((subtotalPence, index) => {
    const qualifies = thresholdPence === 0 || subtotalPence >= thresholdPence;
    let expected = "Confirm the promised item and its eligibility rules";

    if (input.offerType === "percentage") {
      const discountPence = qualifies ? Math.round(subtotalPence * percentageHundredths / 10000) : 0;
      expected = qualifies
        ? `${moneyFromPence(discountPence)} discount; customer pays ${moneyFromPence(subtotalPence - discountPence)}`
        : `No discount; customer pays ${moneyFromPence(subtotalPence)}`;
    } else if (input.offerType === "fixed") {
      const discountPence = qualifies ? Math.min(subtotalPence, fixedDiscountPence) : 0;
      expected = qualifies
        ? `${moneyFromPence(discountPence)} discount; customer pays ${moneyFromPence(subtotalPence - discountPence)}`
        : `No discount; customer pays ${moneyFromPence(subtotalPence)}`;
    } else if (input.offerType === "shipping") {
      expected = qualifies ? "Free delivery should be available" : "Normal delivery charge should remain";
    } else if (input.offerType === "bundle") {
      expected = qualifies
        ? "Check the configured bundle or gift conditions; spend alone does not establish eligibility"
        : "Check whether the configured bundle or gift conditions exclude this basket";
    }

    const labels = thresholdPence > 0
      ? ["Just below the threshold", "Exactly on the threshold", "Comfortably above the threshold"]
      : ["Smaller basket", "Typical basket", "Larger basket"];

    return {
      title: labels[index],
      basket: moneyFromPence(subtotalPence),
      expected,
      test: input.discountMethod === "code"
        ? "Test once without the code, then again with the exact published code."
        : "Test in a fresh session and confirm the automatic promotion appears before payment."
    };
  });
}

function buildLaunchChecklist(input) {
  const start = new Date(input.startAt);
  const validStart = !Number.isNaN(start.getTime());
  const dateFor = (daysBefore) => {
    if (!validStart) return daysBefore === 0 ? "Launch" : `T${daysBefore}`;
    const date = new Date(start.getTime() + daysBefore * DAY);
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(date);
  };

  return [
    { when: dateFor(-14), task: "Lock the offer rules, exclusions, channel wording and approval owner." },
    { when: dateFor(-7), task: "Build the promotion and run the three basket scenarios in a test order." },
    { when: dateFor(-3), task: "Check landing pages and every tracked campaign destination on mobile." },
    { when: dateFor(-1), task: "Brief customer service and confirm who can pause or reverse the campaign." },
    { when: dateFor(0), task: "Run a clean-session purchase, confirm tracking and record the launch decision." },
    { when: dateFor(1), task: "Review customer questions, failed codes and channel-level tracking gaps." }
  ];
}

function evaluateCampaign(input, now = new Date()) {
  const findings = [];
  const start = new Date(input.startAt);
  const end = new Date(input.endAt);
  const validDates = !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime());
  const daysToLaunch = validDates ? Math.ceil((start.getTime() - now.getTime()) / DAY) : null;
  const channels = Array.isArray(input.channels) ? input.channels : [];

  if (!validDates || end <= start) {
    addFinding(findings, "red", "Timing", "The campaign window cannot be executed as entered", "The finish must be later than the start before scheduling or briefing can be reliable.", "Correct the campaign dates and record the timezone used by Shopify and each channel.");
  } else if (input.timezoneConfirmed !== "yes") {
    addFinding(findings, "amber", "Timing", "Channel launch times still need checking", "Shopify, email, paid media and scheduled social posts may start or finish at different moments if their timezone settings differ.", "Check each channel schedule and its timezone, then confirm all activity starts and finishes at the intended time.");
  } else {
    addFinding(findings, "green", "Timing", "Channel timings are confirmed in your plan", "You have confirmed that all channel timings are aligned. This tool has not checked the schedules.", "Keep the same window in every channel schedule.");
  }

  if (input.exclusions === "unclear") {
    addFinding(findings, "red", "Offer", "Customers cannot reliably predict what qualifies", "Unclear exclusions create failed codes, complaints and inconsistent channel copy.", "Name excluded products, collections and gift cards in one canonical offer statement.");
  } else if (input.exclusions === "clear") {
    addFinding(findings, "amber", "Offer", "Exclusions need a deliberate edge-case test", "A clear written rule can still be configured differently in Shopify.", "Add one excluded item to a qualifying basket and confirm it receives no benefit.");
  } else {
    addFinding(findings, "green", "Offer", "The offer has no product exclusions", "The customer promise is easier to understand and test.", "Confirm gift cards and delivery charges behave as intended.");
  }

  if (input.stacking === "unknown") {
    addFinding(findings, "red", "Offer", "Discount stacking has no agreed outcome", "A second code, automatic discount or loyalty benefit may change the customer price unexpectedly.", "Decide which benefits may combine, then test an eligible and an ineligible combination.");
  } else if (input.stacking === "intentional") {
    addFinding(findings, "amber", "Offer", "Intentional stacking needs a maximum-benefit test", "The best possible combination may be materially stronger than the headline offer.", "Build the most generous valid basket and have someone approve the final customer price.");
  } else {
    addFinding(findings, "green", "Offer", "The stacking decision is explicit", "Testers know that a second benefit should be rejected.", "Confirm the rejection message is understandable rather than a generic error.");
  }

  if (input.landingStatus === "not-started") {
    addFinding(findings, "red", "Journey", "There is no campaign destination to test", "Traffic plans are premature until the landing journey exists.", "Create the smallest complete landing journey before producing channel creative.");
  } else if (input.landingStatus === "draft" || input.mobileTest !== "passed") {
    addFinding(findings, "amber", "Journey", "The customer journey is not signed off", "A draft or untested mobile destination can hide offer wording, variant and checkout problems.", "Run the landing page, product and basket journey at a narrow mobile viewport.");
  } else {
    addFinding(findings, "green", "Journey", "The campaign destination has passed mobile review", "The main acquisition journey is ready for final promotion testing.", "Repeat the check after the last merchandising change.");
  }

  if (channels.length >= 3 && input.trackingStatus !== "complete") {
    addFinding(findings, "red", "Measurement", "Channel comparison will be incomplete", `${channels.length} channels are planned, but tracking is not complete enough to separate their contribution.`, "Create one naming convention and a unique tracked destination for every campaign channel.");
  } else if (input.trackingStatus !== "complete") {
    addFinding(findings, "amber", "Measurement", "Campaign tracking is not complete", "You may see total sales without knowing which activity drove useful traffic.", "Define source, medium and campaign values before links are distributed.");
  } else {
    addFinding(findings, "green", "Measurement", "Campaign links can be compared", "The plan supports a channel-level review after launch.", "Open every final link once and confirm parameters survive redirects.");
  }

  if (input.serviceBrief === "no") {
    addFinding(findings, "red", "Operations", "Customer service will learn the offer from customers", "Questions about exclusions, failed codes and returns will be answered inconsistently.", "Give the team the canonical offer, examples, exclusions and escalation route before launch.");
  } else if (input.serviceBrief === "progress") {
    addFinding(findings, "amber", "Operations", "The service brief is unfinished", "The campaign can still change without the frontline team seeing the final rules.", "Name an owner and schedule the final brief after promotion approval.");
  } else {
    addFinding(findings, "green", "Operations", "Customer service has the campaign rules", "The team can answer predictable questions consistently.", "Include screenshots of successful and rejected promotion states.");
  }

  if (input.rollbackOwner === "none") {
    addFinding(findings, "red", "Operations", "Nobody owns the stop decision", "A broken promotion can continue while teams wait for authority.", "Name one person who can pause traffic and disable the offer.");
  } else if (input.rollbackOwner === "unclear") {
    addFinding(findings, "amber", "Operations", "Rollback responsibility is shared but unclear", "Shared responsibility often delays action during a live incident.", "Record the primary owner, backup and exact rollback steps.");
  } else {
    addFinding(findings, "green", "Operations", "A rollback owner is named", "The campaign can be stopped quickly if the customer outcome is wrong.", "Have the owner rehearse the reversal before launch.");
  }

  if (daysToLaunch !== null && daysToLaunch <= 7 && findings.some((item) => item.severity === "red")) {
    addFinding(findings, "red", "Readiness", "The launch window is close with blocking decisions open", `The campaign is ${daysToLaunch < 0 ? "already within its stated window" : `${daysToLaunch} days from launch`} and still has unresolved red findings.`, "Hold further channel rollout until the red findings have named owners.");
  }

  findings.sort((a, b) => severityRank[a.severity] - severityRank[b.severity]);
  const counts = findings.reduce((acc, item) => ({ ...acc, [item.severity]: acc[item.severity] + 1 }), { red: 0, amber: 0, green: 0 });
  const status = counts.red > 0
    ? { key: "hold", label: "Hold for decisions", explanation: `${counts.red} blocking ${counts.red === 1 ? "issue needs" : "issues need"} an owner before launch.` }
    : counts.amber > 0
      ? { key: "review", label: "Needs final review", explanation: `${counts.amber} checks remain before the campaign is ready to release.` }
      : { key: "ready", label: "Ready for final QA", explanation: "No blocking decisions were found. Complete the basket tests before release." };

  return {
    status,
    counts,
    findings,
    scenarios: calculateBasketScenarios(input),
    checklist: buildLaunchChecklist(input),
    summary: {
      name: input.campaignName || "Black Friday campaign",
      offer: describeOffer(input),
      window: validDates
        ? `${new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(start)} to ${new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(end)}`
        : "Campaign window needs correction",
      channels: channels.length ? channels.join(", ") : "No channels selected",
      decision: status.label,
      immediateActions: findings.filter((item) => item.severity !== "green").slice(0, 3).map((item) => item.action)
    }
  };
}

function describeOffer(input) {
  const value = Number(input.discountValue) || 0;
  const threshold = Number(input.minimumSpend) || 0;
  const method = input.discountMethod === "code" ? "with a published code" : "automatically";
  const minimum = threshold > 0 ? ` on baskets of ${money(threshold)} or more` : "";
  const offers = {
    percentage: `${value}% off${minimum}, applied ${method}`,
    fixed: `${money(value)} off${minimum}, applied ${method}`,
    shipping: `Free delivery${minimum}, applied ${method}`,
    bundle: `Bundle or gift offer${minimum}, applied ${method}`
  };
  return offers[input.offerType] || "Offer needs defining";
}

class BfCampaignPreflight extends HTMLElement {
connectedCallback() {
if (this.dataset.initialised) return;
this.dataset.initialised = 'true';
const root = this;


const form = root.querySelector("#bf-preflight-form");
const planner = root.querySelector("#bf-planner");
const results = root.querySelector("#bf-results");
const errorSummary = root.querySelector("#bf-error-summary");
const gateForm = root.querySelector("#bf-lead-form");
const printButton = root.querySelector("#bf-print-results");
const restartButton = root.querySelector("#bf-restart");
const offerTypeField = form.elements.offerType;
const discountField = root.querySelector("#bf-discount-field");
const discountLabel = root.querySelector("#bf-discount-label");
const discountValueField = form.elements.discountValue;
const discountPrefix = root.querySelector("#bf-discount-prefix");
const discountSuffix = root.querySelector("#bf-discount-suffix");
const stepPanels = [...form.querySelectorAll("[data-bf-step]")];
const stepLabel = root.querySelector("#bf-step-label");
const stepMeter = root.querySelector("#bf-step-meter");
const backButton = root.querySelector("[data-bf-back]");
const nextButton = root.querySelector("[data-bf-next]");
const submitButton = root.querySelector("[data-bf-submit]");
const storageKey = "vs-labs-bf-preflight-v1";
let lastInput = null;
let startTracked = false;
let currentStep = 0;

const labels = {
  minimumSpend: "Minimum spend",
  campaignName: "Campaign name",
  offerType: "Offer type",
  discountValue: "Discount value",
  discountMethod: "How the benefit is applied",
  startAt: "Start date and time",
  endAt: "Finish date and time",
  timezoneConfirmed: "Channel timings",
  channels: "Campaign channels",
  exclusions: "Product exclusions",
  stacking: "Discount stacking",
  landingStatus: "Campaign destination",
  mobileTest: "Mobile journey test",
  trackingStatus: "Campaign tracking",
  serviceBrief: "Customer service brief",
  rollbackOwner: "Rollback owner"
};

function track(action, details = {}) {
  const payload = { event: "vs_lead_magnet", lead_magnet: "bf_campaign_preflight", action, ...details };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  window.dispatchEvent(new CustomEvent("vs-labs:lead-magnet", { detail: payload }));
}

function trackStart() {
  if (startTracked) return;
  startTracked = true;
  track("started");
}

function syncDiscountField() {
  const type = offerTypeField.value;
  const needsValue = offerNeedsValue(type);
  discountField.hidden = !needsValue;
  discountValueField.disabled = !needsValue;
  discountValueField.required = needsValue;
  discountValueField.min = "0.01";
  discountValueField.step = "0.01";
  discountValueField.setCustomValidity("");

  if (type === "percentage") {
    discountLabel.textContent = "Discount percentage";
    discountValueField.max = "100";
    discountPrefix.hidden = true;
    discountSuffix.hidden = false;
  } else if (type === "fixed") {
    discountLabel.textContent = "Cash discount amount";
    discountValueField.max = "1000000";
    discountPrefix.hidden = false;
    discountSuffix.hidden = true;
  }
}

function validateDiscountField() {
  const message = discountValidationMessage(offerTypeField.value, discountValueField.value);
  discountValueField.setCustomValidity(message);
  return message;
}

function fieldMessage(field) {
  if ((field.name || "") === "channels") return "Select at least one channel.";
  if (field === discountValueField && field.validationMessage) return field.validationMessage;
  if (field.tagName === "SELECT") return "Choose an answer.";
  if (field.type === "datetime-local") return "Enter a date and time.";
  return field.validationMessage || "Complete this field.";
}

function fieldLabel(field) {
  if (field === discountValueField) return offerTypeField.value === "percentage" ? "Discount percentage" : "Cash discount amount";
  return labels[field.name || "channels"] || "Required answer";
}

function fieldContainer(field) {
  return (field.name === "channels" ? field.closest(".field") : field.closest(".field")) || field.parentElement;
}

function showFieldError(field) {
  const name = field.name || "required";
  const container = fieldContainer(field);
  let message = container.querySelector(`[data-generated-error="${name}"]`);
  if (!message) {
    message = document.createElement("p");
    message.className = "field-error";
    message.dataset.generatedError = name;
    message.id = `bf-field-error-${name}`;
    container.append(message);
  }
  message.textContent = fieldMessage(field);
  message.hidden = false;
  field.setAttribute("aria-invalid", "true");
  const describedBy = new Set((field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
  describedBy.add(message.id);
  field.setAttribute("aria-describedby", [...describedBy].join(" "));
}

function clearFieldError(field) {
  const name = field.name || "required";
  const container = fieldContainer(field);
  const message = container?.querySelector(`[data-generated-error="${name}"]`);
  if (message) message.hidden = true;
  field.removeAttribute("aria-invalid");
}

function invalidFieldsForStep(step) {
  validateDiscountField();
  const panel = stepPanels[step];
  const invalid = [...panel.querySelectorAll("input:invalid, select:invalid, textarea:invalid")];
  if (step === 2 && !panel.querySelector('input[name="channels"]:checked')) {
    invalid.push(panel.querySelector('input[name="channels"]'));
  }
  return [...new Set(invalid)];
}

function showStep(step, focus = true) {
  currentStep = Math.max(0, Math.min(stepPanels.length - 1, step));
  const names = ["Customer promise", "Release window", "Customer journey"];
  stepPanels.forEach((panel, index) => { panel.hidden = index !== currentStep; });
  stepLabel.textContent = `Step ${currentStep + 1} of ${stepPanels.length} · ${names[currentStep]}`;
  stepMeter.value = currentStep + 1;
  backButton.hidden = currentStep === 0;
  nextButton.hidden = currentStep === stepPanels.length - 1;
  submitButton.hidden = currentStep !== stepPanels.length - 1;
  errorSummary.hidden = true;
  if (focus) {
    const legend = stepPanels[currentStep].querySelector("legend");
    legend.setAttribute("tabindex", "-1");
    legend.focus({ preventScroll: true });
    form.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
  }
}

backButton.addEventListener("click", () => showStep(currentStep - 1));
nextButton.addEventListener("click", () => {
  trackStart();
  syncDiscountField();
  const invalid = invalidFieldsForStep(currentStep);
  if (invalid.length) {
    showErrors(invalid);
    return;
  }
  showStep(currentStep + 1);
});

offerTypeField.addEventListener("change", () => {
  syncDiscountField();
  clearFieldError(discountValueField);
});

form.addEventListener("input", (event) => {
  trackStart();
  if (event.target.name) clearFieldError(event.target);
  if (event.target === discountValueField) validateDiscountField();
});

form.addEventListener("change", (event) => {
  trackStart();
  if (event.target.name) clearFieldError(event.target);
  if (event.target.name === "channels") clearFieldError(form.querySelector('input[name="channels"]'));
});

form.addEventListener("submit", (event) => {
  event.preventDefault();
  trackStart();
  validateDiscountField();
  const invalid = [...form.querySelectorAll("input:invalid, select:invalid, textarea:invalid")];
  const channelCount = form.querySelectorAll('input[name="channels"]:checked').length;
  if (channelCount === 0) invalid.push(form.querySelector('input[name="channels"]'));

  if (invalid.length) {
    const fields = [...new Set(invalid)];
    const firstStep = Number(fields[0].closest("[data-bf-step]")?.dataset.bfStep || 0);
    showStep(firstStep, false);
    showErrors(fields.filter((field) => Number(field.closest("[data-bf-step]")?.dataset.bfStep || 0) === firstStep));
    return;
  }

  errorSummary.hidden = true;
  const data = new FormData(form);
  const input = Object.fromEntries(data.entries());
  input.channels = data.getAll("channels");
  if (!offerNeedsValue(input.offerType)) input.discountValue = "0";
  const report = evaluateCampaign(input);
  lastInput = input;
  renderReport(report);
  planner.hidden = true;
  results.hidden = false;
  track("completed");
  track("result", { status: report.status.key, blockers: report.counts.red, checks: report.counts.amber, confirmed: report.counts.green });
  results.focus();
  results.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
});

function showErrors(fields) {
  fields.forEach(showFieldError);
  const links = fields.map((field) => {
    return `<li><a href="#${field.id}">${escapeHtml(fieldLabel(field))}: ${escapeHtml(fieldMessage(field))}</a></li>`;
  });
  const noun = fields.length === 1 ? "answer needs" : "answers need";
  errorSummary.innerHTML = `<strong>${fields.length} ${noun} attention</strong><p>Start with <a href="#${fields[0].id}">${escapeHtml(fieldLabel(fields[0]))}</a>.</p>${fields.length > 1 ? `<details><summary>Show all ${fields.length} answers</summary><ul>${links.join("")}</ul></details>` : ""}`;
  errorSummary.hidden = false;
  errorSummary.focus();
}

errorSummary.addEventListener("click", (event) => {
  const link = event.target.closest("a[href^='#']");
  if (!link) return;
  const target = root.querySelector(link.getAttribute("href"));
  if (!target) return;
  event.preventDefault();
  const targetStep = Number(target.closest("[data-bf-step]")?.dataset.bfStep || currentStep);
  if (targetStep !== currentStep) showStep(targetStep, false);
  target.focus();
  target.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "center" });
});

function renderReport(report) {
  root.querySelector("#bf-status-label").textContent = report.status.label;
  root.querySelector("#bf-status-explanation").textContent = report.status.explanation;
  root.querySelector("#bf-status-card").dataset.status = report.status.key;
  root.querySelector("#bf-count-red").textContent = report.counts.red;
  root.querySelector("#bf-count-amber").textContent = report.counts.amber;
  root.querySelector("#bf-count-green").textContent = report.counts.green;

  const findingMarkup = (item) => `
    <article class="finding finding--${item.severity}">
      <div class="finding__meta"><span>${item.category}</span><span>${item.severity}</span></div>
      <h3>${escapeHtml(item.title)}</h3>
      <p>${escapeHtml(item.reason)}</p>
      <p class="finding__action"><strong>Do next:</strong> ${escapeHtml(item.action)}</p>
    </article>`;
  const actionable = report.findings.filter((item) => item.severity !== "green");
  const confirmed = report.findings.filter((item) => item.severity === "green");
  root.querySelector("#bf-findings").innerHTML = actionable.length
    ? actionable.map(findingMarkup).join("")
    : '<p class="findings__empty">No blockers or checks remain. Complete the basket tests before release.</p>';
  const confirmations = root.querySelector("#bf-confirmations");
  confirmations.hidden = confirmed.length === 0;
  confirmations.open = false;
  root.querySelector("#bf-confirmations-label").textContent = `${confirmed.length} ${confirmed.length === 1 ? "thing" : "things"} confirmed`;
  root.querySelector("#bf-confirmed-findings").innerHTML = confirmed.map(findingMarkup).join("");

  root.querySelector("#bf-scenarios").innerHTML = report.scenarios.map((scenario, index) => `
    <article class="scenario">
      <span class="scenario__number">0${index + 1}</span>
      <div><h3>${escapeHtml(scenario.title)} · ${escapeHtml(scenario.basket)}</h3>
      <p class="scenario__expected">${escapeHtml(scenario.expected)}</p>
      <p>${escapeHtml(scenario.test)}</p></div>
    </article>`).join("");

  root.querySelector("#bf-checklist").innerHTML = report.checklist.map((item) => `
    <li><span>${escapeHtml(item.when)}</span><p>${escapeHtml(item.task)}</p></li>`).join("");

  const summary = report.summary;
  root.querySelector("#bf-summary").innerHTML = `
    <div><span>Campaign</span><strong>${escapeHtml(summary.name)}</strong></div>
    <div><span>Offer</span><strong>${escapeHtml(summary.offer)}</strong></div>
    <div><span>Window</span><strong>${escapeHtml(summary.window)}</strong></div>
    <div><span>Channels</span><strong>${escapeHtml(summary.channels)}</strong></div>
    <div><span>Preflight decision</span><strong>${escapeHtml(summary.decision)}</strong></div>
    <div class="summary__actions"><span>Immediate actions</span><ol>${summary.immediateActions.map((action) => `<li>${escapeHtml(action)}</li>`).join("") || "<li>Complete the final basket tests.</li>"}</ol></div>`;
}

function saveReportForReturn() {
  if (!lastInput) return;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(lastInput));
  } catch (error) {
    // The report remains printable even if browser storage is unavailable.
  }
}

function restoreReportAfterSignup() {
  const marker = root.querySelector("[data-lead-return]");
  if (!marker) return;
  let input = null;
  try {
    input = JSON.parse(sessionStorage.getItem(storageKey));
    sessionStorage.removeItem(storageKey);
  } catch (error) {
    input = null;
  }
  if (input) {
    lastInput = input;
    const report = evaluateCampaign(input);
    renderReport(report);
    planner.hidden = true;
    results.hidden = false;
  }
  track(marker.dataset.leadReturn === "success" ? "email_success" : "email_error");
  marker.focus({ preventScroll: true });
  marker.scrollIntoView({ block: "center", behavior: "auto" });
}

if (gateForm) gateForm.addEventListener("submit", () => {
  if (!gateForm.checkValidity()) return;
  saveReportForReturn();
  track("email_submission");
});

printButton.addEventListener("click", () => window.print());
restartButton.addEventListener("click", () => {
  results.hidden = true;
  planner.hidden = false;
  showStep(0, false);
  form.querySelector("input, select").focus();
});

syncDiscountField();
showStep(0, false);
restoreReportAfterSignup();

function reducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;"
  })[character]);
}

}
}
if (!customElements.get('bf-campaign-preflight')) customElements.define('bf-campaign-preflight', BfCampaignPreflight);
})();
